<template>
    <div class="sdps-root">
        <!-- 控制列 -->
        <div class="d-flex align-center px-1 mb-1" style="gap: 8px">
            <span class="sdps-label">時間粒度</span>
            <v-select
                v-model="tickSize"
                :items="tickOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                density="compact"
                hide-details
                class="sdps-select"
                style="max-width: 110px"
            />
        </div>

        <!-- 圖表 -->
        <div ref="chartDom" style="height: 220px" />

        <!-- 玩家 toggle -->
        <div class="d-flex flex-wrap mt-1 px-1" style="gap: 0 4px">
            <v-checkbox
                v-for="(e, idx) in entities"
                :key="e.entityId"
                v-model="enabledIds"
                :value="e.entityId"
                density="compact"
                hide-details
                class="flex-0-0 sdps-checkbox"
                :color="playerColors[e.entityId] ?? palette[idx % palette.length]"
            >
                <template #label>
                    <span
                        class="mr-1"
                        style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0"
                        :style="{ background: playerColors[e.entityId] ?? palette[idx % palette.length] }"
                    />
                    <span class="sdps-player-label">{{ e.displayName }}</span>
                </template>
            </v-checkbox>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onUnmounted, type PropType } from "vue";
import highcharts from "highcharts";
import type { Options, SeriesOptionsType } from "highcharts";
import type { EntityDamage } from "@/eventActor";

export type DpsChartEntity = {
    entityId: string;
    displayName: string;
    damages: EntityDamage[];
};

export default defineComponent({
    name: "SummaryDpsChart",
    props: {
        entities: {
            type: Array as PropType<DpsChartEntity[]>,
            required: true,
        },
        /** 戰鬥開始/結束時間（秒），用於對齊 X 軸 */
        startAt:      { type: Number, required: true },
        endAt:        { type: Number, required: true },
        /** entityId → color，由父元件統一傳入以保持顏色一致 */
        playerColors: { type: Object as PropType<Record<string, string>>, default: () => ({}) },
    },
    setup(props) {
        const chartDom = ref<HTMLElement>(undefined!);
        let chart: Highcharts.Chart | undefined;

        const TICK_KEY = "summaryDpsChart.tickSize";
        const tickSize = ref(Number(localStorage.getItem(TICK_KEY)) || 15);
        const tickOptions = [
            { label: "5s",   value: 5   },
            { label: "10s",  value: 10  },
            { label: "15s",  value: 15  },
            { label: "30s",  value: 30  },
            { label: "1min", value: 60  },
            { label: "2min", value: 120 },
        ];

        // 預設全部開啟
        const enabledIds = ref<string[]>([]);
        watch(
            () => props.entities,
            (es) => {
                // 新增玩家時預設勾選；移除的玩家也同步清掉
                const ids = es.map((e) => e.entityId);
                enabledIds.value = ids.filter(
                    (id) => enabledIds.value.includes(id) || !enabledIds.value.length,
                );
                if (!enabledIds.value.length) enabledIds.value = [...ids];
            },
            { immediate: true },
        );

        // ── 資料計算 ────────────────────────────────────────────────

        const buildSeries = (): SeriesOptionsType[] => {
            const tick  = tickSize.value;
            const start = props.startAt;
            const end   = props.endAt;

            return props.entities
                .map((e, idx): SeriesOptionsType | null => {
                    if (!enabledIds.value.includes(e.entityId)) return null;

                    const bins = new Map<number, number>();
                    for (const d of e.damages) {
                        if (d.At < start || d.At > end) continue;
                        const bt = Math.floor(d.At / tick) * tick;
                        bins.set(bt, (bins.get(bt) ?? 0) + d.Damage);
                    }

                    const data: [number, number][] = [];
                    const firstBin = Math.floor(start / tick) * tick;
                    const lastBin  = Math.floor(end   / tick) * tick;
                    for (let t = firstBin; t <= lastBin; t += tick) {
                        data.push([t * 1000, Math.round((bins.get(t) ?? 0) / tick)]);
                    }

                    const color = props.playerColors[e.entityId] ?? PALETTE[idx % PALETTE.length];
                    return {
                        type: "area",
                        name: e.displayName,
                        data,
                        color,
                        fillColor: {
                            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                            stops: [
                                [0, highcharts.color(color).setOpacity(0.3).get() as string],
                                [1, highcharts.color(color).setOpacity(0.02).get() as string],
                            ],
                        },
                        lineWidth: 1.5,
                        marker: { enabled: false },
                        threshold: null,
                    };
                })
                .filter((s): s is SeriesOptionsType => s !== null);
        };

        const FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";
        const AXIS_LABEL_COLOR = "rgba(221,225,231,0.55)";
        const GRID_COLOR       = "rgba(255,255,255,0.07)";
        const AXIS_LINE_COLOR  = "rgba(255,255,255,0.10)";

        const buildOptions = (): Options => ({
            title:   { text: "" },
            credits: { enabled: false },
            time:    { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            chart: {
                animation: false,
                zooming: { type: "x" },
                // left=110 matches GanttChart .gantt-label width (flex: 0 0 110px)
                // right=4  matches GanttChart's right edge (no right label)
                margin: [8, 4, 36, 110],
                backgroundColor: "transparent",
                style: { fontFamily: FONT },
            },
            xAxis: {
                type: "datetime",
                labels: {
                    style: { fontSize: "10px", color: AXIS_LABEL_COLOR, fontFamily: FONT },
                },
                lineColor: AXIS_LINE_COLOR,
                tickColor: AXIS_LINE_COLOR,
            },
            yAxis: {
                title: { text: "DPS", style: { fontSize: "10px", color: AXIS_LABEL_COLOR, fontFamily: FONT } },
                labels: {
                    style: { fontSize: "10px", color: AXIS_LABEL_COLOR, fontFamily: FONT },
                    formatter() { return fmtDps(this.value as number); },
                },
                gridLineColor: GRID_COLOR,
                min: 0,
            },
            legend: {
                enabled: false,  // 用 checkbox 取代
            },
            tooltip: {
                shared: true,
                backgroundColor: "#10151e",
                borderColor: "rgba(0,212,200,0.35)",
                borderRadius: 6,
                style: { color: "#dde1e7", fontSize: "11px", fontFamily: FONT },
                formatter() {
                    const pts = (this.points ?? []);
                    const time = new Date(this.x as number).toLocaleTimeString("zh-TW", { hour12: false });
                    const rows = pts.map(
                        (p) => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${fmtDps(p.y ?? 0)}</b>`,
                    ).join("<br>");
                    return `<span style="font-size:10px;opacity:0.6">${time}</span><br>${rows}`;
                },
            },
            plotOptions: {
                area: { stacking: undefined },
            },
            series: buildSeries(),
        });

        const rebuildChart = () => {
            if (!chartDom.value) return;
            chart?.destroy();
            chart = highcharts.chart(chartDom.value, buildOptions());
        };

        onMounted(rebuildChart);

        watch(
            [tickSize, enabledIds, () => props.entities, () => props.startAt, () => props.endAt, () => props.playerColors],
            rebuildChart,
            { deep: true },
        );

        watch(tickSize, (v) => localStorage.setItem(TICK_KEY, String(v)));

        onUnmounted(() => chart?.destroy());

        const playerColors = computed(() => props.playerColors);
        return { chartDom, tickSize, tickOptions, enabledIds, palette: PALETTE, playerColors };
    },
});

const PALETTE = [
    "#00d4c8","#4fc3f7","#81c784","#ffb74d",
    "#ce93d8","#ef9a9a","#80deea","#a5d6a7",
];

function fmtDps(v: number): string {
    if (!isFinite(v) || v <= 0) return "0";
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M/s`;
    if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k/s`;
    return `${Math.round(v)}/s`;
}
</script>

<style scoped>
.sdps-root {
    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

.sdps-label {
    font-size: 11px;
    color: rgba(221, 225, 231, 0.5);
    letter-spacing: 0.04em;
}

.sdps-player-label {
    font-size: 12px;
    color: rgba(221, 225, 231, 0.8);
    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* v-select dark override */
.sdps-select :deep(.v-field__outline) {
    --v-field-border-opacity: 0.25;
}
.sdps-select :deep(.v-field) {
    background: rgba(255, 255, 255, 0.04) !important;
    color: rgba(221, 225, 231, 0.85) !important;
    font-size: 12px;
    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}
.sdps-select :deep(.v-select__selection-text) {
    color: rgba(221, 225, 231, 0.85);
    font-size: 12px;
}
.sdps-select :deep(.v-field__append-inner .v-icon) {
    color: rgba(221, 225, 231, 0.4);
}

/* checkbox label font */
.sdps-checkbox :deep(.v-label) {
    font-size: 12px;
    color: rgba(221, 225, 231, 0.75);
    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}
</style>
